import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { COMPANY, NAV_ITEMS } from "../lib/constants";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = NAV_ITEMS.map((n) => document.getElementById(n.id)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  const handleNavClick = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <button
            data-testid="header-logo"
            onClick={() => handleNavClick("home")}
            className="flex items-center gap-3 group"
            aria-label="Master Business — Início"
          >
            <div className={`rounded-xl p-1.5 transition-all ${scrolled ? "bg-[#111111]" : "bg-black/40 backdrop-blur"}`}>
              <img
                src={COMPANY.logoHorizontal}
                alt="Master Business Consultoria Contábil"
                className="h-10 md:h-11 w-auto object-contain"
              />
            </div>
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-link-${item.id}`}
                onClick={() => handleNavClick(item.id)}
                className={`relative px-4 py-2 text-sm font-semibold tracking-wide transition-colors ${
                  scrolled ? "text-[#111111]" : "text-white"
                } hover:text-[#00C7A5]`}
              >
                {item.label}
                {active === item.id && (
                  <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-[#00C7A5] rounded-full" />
                )}
              </button>
            ))}
          </nav>

          <div className="hidden lg:block">
            <button
              data-testid="header-cta-button"
              onClick={() => handleNavClick("contatos")}
              className="bg-[#00C7A5] hover:bg-[#00A88A] text-white font-semibold text-sm uppercase tracking-wide px-6 py-3 rounded-full shadow-[0_8px_20px_rgba(0,199,165,0.35)] hover:-translate-y-0.5 transition-all"
            >
              Fale com um Especialista
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 rounded-md ${scrolled ? "text-[#111111]" : "text-white"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Abrir menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <nav className="px-4 py-4 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                data-testid={`mobile-nav-link-${item.id}`}
                onClick={() => handleNavClick(item.id)}
                className="text-left px-4 py-3 text-[#111111] font-semibold hover:bg-[#F5F7FA] rounded-lg"
              >
                {item.label}
              </button>
            ))}
            <button
              data-testid="mobile-cta-button"
              onClick={() => handleNavClick("contatos")}
              className="mt-2 bg-[#00C7A5] hover:bg-[#00A88A] text-white font-semibold uppercase tracking-wide px-6 py-3 rounded-full"
            >
              Fale com um Especialista
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
